import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getCompanies, removeCompany } from '$/store/company/actions';
import { AppState } from '$/store';
import * as Bixby from '$/core';
import '$/pages/admin/company/index.scss';
import './index.scss';
import CreateCompany from '$/pages/admin/company/add-company/CreateCompany';
import HeadingBlock from '$/components/global/heading';
import DeleteButton from '$/components/grid/DeleteButton';
import { Link } from 'react-router-dom';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { getAllPublicAdminCompaniesState } from '$/store/company/selectors';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi } from 'ag-grid-enterprise';

interface ICompanyProps {
  isLoading: boolean;
  error?: Bixby.Models.BixbyError;
  companies: Bixby.Models.AdminCompanySlim[] | undefined;

  dispatchGetCompanies: () => Promise<void>;
  dispatchRemoveCompany: (companySlim: Bixby.Models.AdminCompanySlim) => Promise<void>;
}

const PublicCompanyList: React.FC<ICompanyProps> = props => {
  const { companies, dispatchGetCompanies, dispatchRemoveCompany, isLoading } = props;
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState({} as Bixby.Models.AdminCompanySlim | undefined);
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  useEffect(() => {
    const getCompaniesDispatcher = async () => {
      await dispatchGetCompanies();
      setIsCreatingCompany(false);
    };
    getCompaniesDispatcher();
  }, [dispatchGetCompanies]);

  const handleDeleteCompany = async () => {
    if (companyToDelete && !Bixby.Utils.isNullOrEmpty(companyToDelete)) {
      await dispatchRemoveCompany(companyToDelete);
      await dispatchGetCompanies();
      setCompanyToDelete(undefined);
    }
    setDeleteModalActive(false);
  };

  const handleOpenAddModal = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    setIsCreatingCompany(true);
  };

  const handleCloseModal = () => {
    setIsCreatingCompany(false);
    setDeleteModalActive(false);
    setCompanyToDelete(undefined);
  };

  const companyNameCellRenderer = (props: any) => (
    <Link className="link_underline" to={`/admin/company/${props.data.id}`}>
      {props.data.name}
    </Link>
  );
  const websiteCellRenderer = (props: any) => (
    <a className="link_underline" href={props.data.websiteUrl} target="_blank">
      {props.data.websiteUrl}
    </a>
  );
  const handleDeleteButton = (row: Bixby.Models.AdminCompanySlim) => {
    setCompanyToDelete(row);
    setDeleteModalActive(true);
    return Promise.resolve();
  };
  const deleteButtonCellRenderer = (props: any) => <DeleteButton row={props.data} deleter={handleDeleteButton} />;

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <>
        <HeadingBlock
          title={'Manage Public Companies'}
          addButton={{ enabled: true, text: 'Add New Public Company', onClick: handleOpenAddModal }}
        />
        <div className="public_company_list">
          <div className="grid_container">
            <div className="grid ag-theme-alpine">
              {companies && !Bixby.Utils.isNullOrEmpty(companies) ? (
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: true,
                    resizable: true,
                    lockPinned: true,
                    filter: 'agNumberColumnFilter',
                    cellClass: 'align-center',
                  }}
                  onGridReady={onGridReady}
                  rowData={companies}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={35}
                  headerHeight={35}
                  components={{
                    companyNameCellRenderer,
                    websiteCellRenderer,
                    deleteButtonCellRenderer,
                  }}
                  columnDefs={[
                    {
                      headerName: '',
                      cellClass: 'align-left',
                      cellRenderer: 'deleteButtonCellRenderer',
                      maxWidth: 65,
                    },
                    {
                      field: 'name',
                      headerName: 'Company Name',
                      cellClass: 'align-left',
                      minWidth: 100,
                      cellRenderer: 'companyNameCellRenderer',
                    },
                    {
                      field: 'websiteUrl',
                      headerName: 'Investor Relations Site',
                      cellClass: 'align-left',
                      minWidth: 100,
                      cellRenderer: 'websiteCellRenderer',
                    },
                  ]}></AgGridReact>
              ) : (
                <NoDataFound dataType="companies" />
              )}
              {companyToDelete && (
                <Modal title="" active={deleteModalActive} onClose={handleCloseModal}>
                  <p>Are you sure you want to delete {companyToDelete.name}?</p>
                  <div className="button_side_wrap">
                    <BixButton onClick={handleDeleteCompany}>Delete Company</BixButton>
                    <BixButton light={true} onClick={handleCloseModal}>
                      Cancel
                    </BixButton>
                  </div>
                </Modal>
              )}
              <CreateCompany
                onCloseModal={handleCloseModal}
                isCreatingCompany={isCreatingCompany}
                defaultCompanyType="public"
              />
            </div>
          </div>
        </div>
      </>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  const { companies } = getAllPublicAdminCompaniesState(state);
  return {
    companies,
    isLoading: state.companyState.isLoading,
    error: state.companyState.error,
    company: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetCompanies: () => dispatch(getCompanies()),
  dispatchRemoveCompany: (company: Bixby.Models.AdminCompanySlim) => dispatch(removeCompany(company)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublicCompanyList);
